import { useMemo } from 'react';
import useCurrentLocale from './useCurrentLocale';
import { getLangFromLocale, getTranslations } from '@/lib/utils/translations';
import { Translations } from '@/lib/types/translations';
import OutLink from '../components/icons/OutLink';

const translations = {
  sv: {
    // Banner
    bannerTitle: 'Byt in din gamla enhet',
    bannerSubTitle:
      'Få pengar direkt till ditt bankkonto. Fraktfritt, säkert och bra för miljön!',
    bannerButton: 'Se vad din enhet är värd',
    bannerBonusTitle: 'Just nu',
    bannerBonusDesc: 'extra vid varje inbyte',

    completedTitle: 'Redo för inbyte',
    completedDescription:
      'Efter avslutat köp kommer du att få ett mail med instruktioner för hur du går vidare',
    completedRedo: 'Gör om',

    // Navigation
    close: 'Stäng',
    next: 'Nästa',
    price: 'Värdera',
    back: 'Tillbaka',
    submit: 'Skicka',

    // Menu
    categoryTitle: 'Spara pengar genom att byta in din gamla enhet',
    categorySubTitle: 'Enheter från alla tillverkare är välkomna!',
    mobile: 'Mobil',
    tablet: 'Tablet',
    watch: 'Klocka',
    computer: 'Laptop',

    // Model select
    modelTitle: 'Sök modell',
    modelMobileTitle: 'Sök modell eller ange IMEI-nummer',
    modelMobilePlaceholder: '',
    modelImeiTitel: 'Var hittar jag IMEI-numret?',
    modelImeiStep1Prefix: 'Slå',
    modelImeiStep1Suffix: 'på din mobil',
    modelImeiStep2:
      'Information om enhetens IMEI- och serienummer visas automatiskt på skärmen.',
    modelPlaceholder: '',
    'modelError.deviceNotFound': 'Vi lyckades inte hitta din modell',

    // Grading
    grading: 'Värdering',
    gradingTitle: 'Hur väl fungerar din enhet?',
    gradingExplainQuestion: 'Vad betyder detta?',
    gradingYes: 'Ja',
    gradingNo: 'Nej',

    // Contact form
    contactFormTitle: 'Registrera ditt inbyte',
    contactFormSubTitle:
      'Efter avslutat köp kommer du att få ett mail med instruktioner för hur du går vidare',
    firstName: 'Förnamn',
    lastName: 'Efternamn',
    address: 'Adress',
    postalCode: 'Postnummer',
    city: 'Stad',
    email: 'Din e-postadress',
    iban: 'Var vill du att pengarna ska sättas in?',
    ibanDescription: 'Ditt bankkontos IBAN-nummer',
    ibanPlaceholder: 'SE0000000000000000000000',
    termsAndConditions: (termsLink: string, policyLink: string) => (
      <>
        Jag godkänner{' '}
        <a
          href={termsLink}
          aria-label="Information om villkoren, Öppnas i ny flik"
          className="text-samsungBlue underline"
          target="_blank"
        >
          villkoren
          <OutLink className="w-3 h-3 inline-block ml-0.5" />
        </a>{' '}
        och{' '}
        <a
          href={policyLink}
          aria-label="Information om integritetspolicyn, Öppnas i ny flik"
          className="text-samsungBlue underline"
          target="_blank"
        >
          integritetspolicyn
          <OutLink className="w-3 h-3 inline-block ml-0.5" />
        </a>
      </>
    ),

    marketingConsent: (partner: string) =>
      `Jag vill gärna ta emot marknadsföring från Samsung om Samsung Trade-In hos ${partner} via e-post.`,
    marketingUnsubscribe:
      'Jag kan när som helst avregistrera mig via länken i e-postmeddelandet.',

    // Completed Trade in
    successTradeInTitle: 'Lyckad registrering med ID:',
    completeTitle: 'Lyckad registrering',

    errorTradeIn: 'Något gick fel med registreringen, kontakta kundtjänst',

    // Screen reader arias
    SRCloseModal: 'Stäng dialog',

    // Item identify
    imeiNumber: 'IMEI-nummer',
    imeiPlaceholder: 'Mobilens 15-siffriga IMEI-nummer',
    serialNumber: 'Serienummer',
    'deviceIdentifierError.UnknownError':
      'Felaktigt format - kontrollera att du har skrivit rätt',
    'deviceIdentifierError.DeviceIdentifierInvalid':
      'Felaktigt format - kontrollera att du har skrivit rätt',
    'deviceIdentifierError.DeviceIdentifierInvalidLength':
      'Felaktig längd - kontrollera att du skrivit minst 5 tecken',
    'deviceIdentifierError.DeviceAlreadyRegistered':
      'Enheten är redan registrerad och kan inte registreras igen',
    updatePrice: 'Uppdatera pris',

    // Generic
    loading: 'Laddar',
    send: 'Skicka',
    opensInTab: 'Öppnas i ny flik',

    // How it works
    howOneDescription: 'Ta reda på hur mycket du kan spara',
    howTwoDescription: 'Slutför ditt köp och kom igång med din nya enhet',
    howThreeDescription: 'Skicka inbytesenheten inom 14 dagar',
    howFourDescription: 'Få pengar tillbaka',
    howLinkText: 'Så fungerar inbyte',

    // SelectedDevice
    priceEstimate: 'Uppskattat värde',
    priceCampaign: 'Kampanj',
    priceGrading: 'Kan ändras efter slutgiltig värdering',
  },
  da: {
    // Banner
    bannerTitle: 'Byt din gamle enhed',
    bannerSubTitle:
      'Få penge direkte på din bankkonto. Gratis fragt, sikkert og godt for miljøet!',
    bannerButton: 'Se hvad din enhed er værd',
    bannerBonusTitle: 'Lige nu',
    bannerBonusDesc: 'ekstra ved hver indbytning',

    completedTitle: 'Klar til bytte',
    completedDescription:
      'Efter dit køb vil du modtage en e-mail med instruktioner om, hvordan du går videre',
    completedRedo: 'Gentag',

    // Navigation
    close: 'Luk',
    next: 'Næste',
    back: 'Tilbage',
    submit: 'Send',

    // Menu
    categoryTitle: 'Spar penge ved at bytte din gamle enhed',
    categorySubTitle: 'Enheder fra alle producenter er velkomne!',
    mobile: 'Mobil',
    price: 'Vurdere',
    tablet: 'Tablet',
    watch: 'Ur',
    computer: 'Bærbar',

    // Model select
    modelTitle: 'Søg model',
    modelMobileTitle: 'Søg model eller indtast IMEI-nummer',
    modelMobilePlaceholder: '',

    modelImeiTitel: 'Hvor finder jeg IMEI-nummeret?',
    modelImeiStep1Prefix: 'Indtast',
    modelImeiStep1Suffix: 'på din mobil',
    modelImeiStep2:
      'Oplysninger om enhedens IMEI- og serienummer vises automatisk på skærmen.',

    modelPlaceholder: '',
    'modelError.deviceNotFound': 'Vi kunne ikke finde din model',

    // Grading
    grading: 'Vurdering',
    gradingTitle: 'Hvor godt fungerer din enhed?',
    gradingExplainQuestion: 'Hvad betyder dette?',
    gradingYes: 'Ja',
    gradingNo: 'Nej',

    // Contact form
    contactFormTitle: 'Registrer din bytte',
    contactFormSubTitle:
      'Efter dit køb vil du modtage en e-mail med instruktioner om, hvordan du går videre',
    firstName: 'Fornavn',
    lastName: 'Efternavn',
    address: 'Adresse',
    postalCode: 'Postnummer',
    city: 'By',
    email: 'Din e-mailadresse',
    iban: 'Hvilken konto skal pengene overføres til?',
    ibanDescription: 'Dit bankkontos IBAN-nummer',
    ibanPlaceholder: 'DK0000000000000000',
    termsAndConditions: (termsLink: string, policyLink: string) => (
      <p>
        Jeg accepterer{' '}
        <a
          href={termsLink}
          className="text-samsungBlue underline"
          aria-label="Information om vilkårene, åbner i ny fane"
          target="_blank"
        >
          vilkårene
          <OutLink className="w-3 h-3 inline-block ml-0.5" />
        </a>{' '}
        og{' '}
        <a
          href={policyLink}
          className="text-samsungBlue underline"
          aria-label="Information om privatlivspolitikken, åbner i ny fane"
          target="_blank"
        >
          privatlivspolitikken
          <OutLink className="w-3 h-3 inline-block ml-0.5" />
        </a>
      </p>
    ),
    marketingConsent: (partner: string) =>
      `Ja tak, jeg vil gerne modtage markedsføring fra Samsung om Samsung Trade-In hos ${partner} via e-mail.`,
    marketingUnsubscribe:
      'Jeg kan til enhver tid afmelde via linket i e-mailen.',

    // Completed Trade in
    successTradeInTitle: 'Succesfuld registrering med ID:',
    completeTitle: 'Succesfuld registrering',

    errorTradeIn: 'Noget gik galt med registreringen, kontakt kundeservice',

    // Screen reader arias
    SRCloseModal: 'Luk dialog',

    // Item identify
    imeiNumber: 'IMEI-nummer',
    imeiPlaceholder: 'Mobilens 15-cifrede IMEI-nummer',
    serialNumber: 'Serienummer',
    'deviceIdentifierError.UnknownError':
      'Forkert format - tjek, at du har skrevet korrekt',
    'deviceIdentifierError.DeviceIdentifierInvalid':
      'Forkert format - tjek, at du har skrevet korrekt',
    'deviceIdentifierError.DeviceIdentifierInvalidLength':
      'Forkert længde - tjek, at du har skrevet mindst 5 tegn',
    'deviceIdentifierError.DeviceAlreadyRegistered':
      'Enheden er allerede registreret, og kan ikke registreres igen',
    updatePrice: 'Opdater pris',

    // Generic
    loading: 'Indlæser',
    send: 'Send',
    opensInTab: 'Åbner i ny fane',

    // How it works
    howOneDescription: '1. Find ud af, hvor meget du kan spare',
    howTwoDescription: '2. Gennemfør dit køb og kom i gang med din nye enhed',
    howThreeDescription: '3. Send din gamle enhed inden for 14 dage',
    howFourDescription: '4. Få penge tilbage',
    howLinkText: 'Sådan fungerer det',

    // SelectedDevice
    priceEstimate: 'Estimeret værdi',
    priceCampaign: 'Kampagne',
    priceGrading: 'Kan ændres efter endelig vurdering',
  },
  fi: {},
  no: {},
} satisfies Translations;

const useTranslation = () => {
  const locale = useCurrentLocale();
  const lang = getLangFromLocale(locale);

  // Temporary solution until we have all translations
  return useMemo(() => getTranslations(translations, lang), [lang]);
};

export default useTranslation;
